import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { apiOrigin } from '../apiOrigin';
import api from '../services/api';
import { computeBadges, mergeInboxEvent, removeDecidedDocument, removeDecidedSelfie } from '../lib/reviewLogic.mjs';

// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001 (Phase D / R1 Stufe F) — PERSISTENTE Review-Inbox als App-weiter Context.
// Quelle der Wahrheit: GET /api/admin/review-inbox (ungelesene admin_review_inbox-Events).
// Live: Socket-Client — der Server sendet 'review_inbox_changed' jetzt an den berechtigungs-
// gescopten Raum 'reviews' (NICHT mehr 'admins'). Der Server tritt berechtigte Admins beim connect
// automatisch bei UND hört auf 'join_reviews' (deckungsgleicher Vertrag); der Client emittiert es
// zusätzlich beim connect. HTTP-Polling alle 30 s bleibt als
// Fallback. Der Zähler sinkt ERST nach ERFOLGREICHER Entscheidung (documentDecided/
// selfieDecided → optimistisch räumen + Server-Refresh), NIE beim bloßen Ansehen.
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY_BADGES = { fahrer: 0, partner: 0, fahrzeug: 0, selfie: 0, total: 0 };
const ReviewInboxContext = createContext({
  badges: EMPTY_BADGES, events: [], live: false,
  refresh: () => {}, documentDecided: () => {}, selfieDecided: () => {},
});
export const useReviewInbox = () => useContext(ReviewInboxContext);

export function ReviewInboxProvider({ children }) {
  const [events, setEvents] = useState([]);
  const [live, setLive] = useState(false);

  const refresh = useCallback(() => {
    api.get('/admin/review-inbox')
      .then((r) => setEvents(r.data.events || []))
      .catch(() => {});   // Inbox ist Komfort — Fehler blockieren die Seite nie
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, 30000);   // Polling-Fallback

    // Socket-Pfad (wie LiveMap): Admin-Token → Server. Für Review-Live-Badges MUSS der Client dem
    // berechtigungsgescopten Raum 'reviews' beitreten (join_reviews) — auch nach Reconnect.
    const socket = io(apiOrigin(), {
      auth: { token: localStorage.getItem('admin_token') },
      extraHeaders: { 'ngrok-skip-browser-warning': 'true' },
    });
    socket.on('connect', () => { setLive(true); socket.emit('join_reviews'); refresh(); });   // Raum 'reviews' beitreten + frisch ziehen
    socket.on('disconnect', () => setLive(false));
    socket.on('review_inbox_changed', (payload) => {
      // Neues Event vorne einsortieren (idempotent); ohne Event-Payload lieber neu laden.
      if (payload?.event) setEvents((prev) => mergeInboxEvent(prev, payload.event));
      else refresh();
    });

    return () => { clearInterval(poll); socket.disconnect(); };
  }, [refresh]);

  // ERFOLGREICHE Dokument-Entscheidung → Event optimistisch räumen + Server-Stand ziehen
  // (der Server hat read_at bereits in derselben Transaktion gesetzt).
  const documentDecided = useCallback((documentId) => {
    setEvents((prev) => removeDecidedDocument(prev, documentId));
    refresh();
  }, [refresh]);

  // ERFOLGREICHE Selfie-Entscheidung (document_id = Verifizierungs-ID).
  const selfieDecided = useCallback((verificationId) => {
    setEvents((prev) => removeDecidedSelfie(prev, verificationId));
    refresh();
  }, [refresh]);

  const badges = computeBadges(events);

  return (
    <ReviewInboxContext.Provider value={{ badges, events, live, refresh, documentDecided, selfieDecided }}>
      {children}
    </ReviewInboxContext.Provider>
  );
}
